/**
 * /payroll-l2/forms — IRS compliance form summaries: 941, 940, W-2.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/forms")({
  component: FormsPage,
});

function fmtMoney(v: string | null | undefined) {
  if (!v) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

// ── 941 ────────────────────────────────────────────────────────────────────

interface Form941 {
  entity_id: string;
  quarter: number;
  year: number;
  total_wages: string;
  total_federal_income_tax: string;
  total_ss_wages: string;
  total_ss_tax: string;
  total_medicare_wages: string;
  total_medicare_tax: string;
  total_tax_liability: string;
  total_deposits: string;
  balance_due: string;
  is_estimate: boolean;
}

function Form941Panel() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [quarter, setQuarter] = useState(1);
  const [fetch, setFetch] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "form-941", year, quarter],
    queryFn: async () =>
      api<Form941>(`/api/payroll-l2/forms/941?year=${year}&quarter=${quarter}`),
    enabled: fetch,
  });

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>Form 941 — Employer's Quarterly Federal Tax Return</Typography>
      <Stack direction="row" gap={2} mb={3} alignItems="flex-end">
        <TextField select label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} size="small" sx={{ width: 120 }}>
          {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <TextField select label="Quarter" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} size="small" sx={{ width: 120 }}>
          {[1, 2, 3, 4].map((q) => <MenuItem key={q} value={q}>Q{q}</MenuItem>)}
        </TextField>
        <Button variant="contained" onClick={() => setFetch(true)}>
          Generate
        </Button>
      </Stack>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">No pay runs found for this period, or generation failed.</Alert>}

      {data && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            {year} Q{quarter} — {["Jan–Mar", "Apr–Jun", "Jul–Sep", "Oct–Dec"][quarter - 1]}
          </Typography>
          {data.is_estimate && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Summary view — SS/Medicare wages are back-computed from withholding
              and `Total Deposits` reflects liability, not actual EFTPS deposits.
              Reconcile against bank records before filing.
            </Alert>
          )}
          <Table size="small">
            <TableBody>
              {[
                ["Total Wages, Tips, and Other Compensation (Line 2)", fmtMoney(data.total_wages)],
                ["Federal Income Tax Withheld (Line 3)", fmtMoney(data.total_federal_income_tax)],
                ["Social Security Wages (Line 5a)", fmtMoney(data.total_ss_wages)],
                ["Social Security Tax (Line 5a col 2)", fmtMoney(data.total_ss_tax)],
                ["Medicare Wages (Line 5c)", fmtMoney(data.total_medicare_wages)],
                ["Medicare Tax (Line 5c col 2)", fmtMoney(data.total_medicare_tax)],
                ["Total Tax Liability (Line 6)", fmtMoney(data.total_tax_liability)],
                ["Total Deposits Made (Line 13)", fmtMoney(data.total_deposits)],
                ["Balance Due (Line 14)", fmtMoney(data.balance_due)],
              ].map(([label, val]) => (
                <TableRow key={label as string}>
                  <TableCell sx={{ color: "text.secondary" }}>{label}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontFamily: "monospace" }}>{val}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button startIcon={<DownloadIcon />} size="small" sx={{ mt: 2 }} disabled>
            Download PDF (coming soon)
          </Button>
        </Paper>
      )}
    </Box>
  );
}

// ── 940 ────────────────────────────────────────────────────────────────────

interface Form940 {
  entity_id: string;
  year: number;
  total_futa_wages: string;
  futa_tax_before_adjustments: string;
  adjustments: string;
  net_futa_tax: string;
  total_deposits: string;
  balance_due: string;
  is_estimate: boolean;
}

function Form940Panel() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [fetch, setFetch] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "form-940", year],
    queryFn: async () => api<Form940>(`/api/payroll-l2/forms/940?year=${year}`),
    enabled: fetch,
  });

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>Form 940 — Employer's Annual FUTA Tax Return</Typography>
      <Stack direction="row" gap={2} mb={3} alignItems="flex-end">
        <TextField select label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} size="small" sx={{ width: 120 }}>
          {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <Button variant="contained" onClick={() => setFetch(true)}>
          Generate
        </Button>
      </Stack>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">No pay runs found for this year, or generation failed.</Alert>}

      {data && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Calendar Year {data.year}
          </Typography>
          <Table size="small">
            <TableBody>
              {[
                ["Total Payments to All Employees (Line 3)", fmtMoney(data.total_futa_wages)],
                ["FUTA Tax Before Adjustments (Line 8)", fmtMoney(data.futa_tax_before_adjustments)],
                ["Adjustments (Lines 9–11)", fmtMoney(data.adjustments)],
                ["Net FUTA Tax (Line 12)", fmtMoney(data.net_futa_tax)],
                ["Total FUTA Deposits (Line 13)", fmtMoney(data.total_deposits)],
                ["Balance Due (Line 14)", fmtMoney(data.balance_due)],
              ].map(([label, val]) => (
                <TableRow key={label as string}>
                  <TableCell sx={{ color: "text.secondary" }}>{label}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontFamily: "monospace" }}>{val}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button startIcon={<DownloadIcon />} size="small" sx={{ mt: 2 }} disabled>
            Download PDF (coming soon)
          </Button>
        </Paper>
      )}
    </Box>
  );
}

// ── W-2 ────────────────────────────────────────────────────────────────────

interface W2Row {
  employee_id: string;
  year: number;
  wages_tips: string;
  federal_income_tax: string;
  ss_wages: string;
  ss_tax: string;
  medicare_wages: string;
  medicare_tax: string;
  state: string | null;
  state_wages: string | null;
  state_income_tax: string | null;
  is_estimate: boolean;
}

function W2Panel() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [fetch, setFetch] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "form-w2", year],
    queryFn: async () =>
      api<W2Row[]>("/api/payroll-l2/forms/w2", { method: "POST", body: { year } }),
    enabled: fetch,
  });

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>W-2 — Wage and Tax Statement</Typography>
      <Stack direction="row" gap={2} mb={3} alignItems="flex-end">
        <TextField select label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} size="small" sx={{ width: 120 }}>
          {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <Button variant="contained" onClick={() => setFetch(true)}>
          Generate
        </Button>
      </Stack>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">No payroll data found for this year.</Alert>}

      {data && data.length === 0 && (
        <Alert severity="info">No pay runs posted for {year}.</Alert>
      )}

      {data && data.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell align="right">Box 1 Wages</TableCell>
              <TableCell align="right">Box 2 Fed Tax</TableCell>
              <TableCell align="right">Box 3 SS Wages</TableCell>
              <TableCell align="right">Box 4 SS Tax</TableCell>
              <TableCell align="right">Box 5 Med Wages</TableCell>
              <TableCell align="right">Box 6 Med Tax</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.employee_id} hover>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{row.employee_id.slice(0, 8)}</TableCell>
                <TableCell align="right">{fmtMoney(row.wages_tips)}</TableCell>
                <TableCell align="right">{fmtMoney(row.federal_income_tax)}</TableCell>
                <TableCell align="right">{fmtMoney(row.ss_wages)}</TableCell>
                <TableCell align="right">{fmtMoney(row.ss_tax)}</TableCell>
                <TableCell align="right">{fmtMoney(row.medicare_wages)}</TableCell>
                <TableCell align="right">{fmtMoney(row.medicare_tax)}</TableCell>
                <TableCell>
                  <Button size="small" startIcon={<DownloadIcon />} disabled>PDF</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

function FormsPage() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>Compliance Forms</Typography>
        <HelpHint docPath="payroll-l2/employee-forms.md" label="Employee forms help" />
      </Stack>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Form 941 (Quarterly)" />
        <Tab label="Form 940 (Annual FUTA)" />
        <Tab label="W-2 (Annual)" />
      </Tabs>

      {tab === 0 && <Form941Panel />}
      {tab === 1 && <Form940Panel />}
      {tab === 2 && <W2Panel />}
    </Box>
  );
}
